import { useState } from 'react';
import { useIntegrations } from '../../../hooks/useIntegrations';
import { Box, Stack } from '@mui/material';
import { colors, Switch, Typography } from '@sweep-io/sweep-design';
import { SearchInput } from '../../common/SearchInput';
import { useRunOnce } from '../../common/useRunOnce';
import pluralize from 'pluralize';
import { telemetry } from '../../../telemetry';
import { CenteredCircularProgress } from '../../common/CenteredCircularProgress';

export const TeamsInstallationContent = () => {
  const [teams, setTeams] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const { getTeamsWithInstallationStatus, installTeamsApp, uninstallTeamsApp } = useIntegrations();

  useRunOnce(() => {
    fetchTeams();
  });
  const fetchTeams = async () => {
    setLoading(true);
    try {
      const data = await getTeamsWithInstallationStatus();
      setAllTeams(data);
      setTeams(data);
    } catch (e) {
      telemetry.captureError(e);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (team: any) => {
    try {
      let response;
      if (team.isSweepAppInstalled) {
        response = await uninstallTeamsApp([team]);
      } else {
        response = await installTeamsApp([team]);
      }

      if (response && response.length > 0) {
        const updatedTeam = response[0];
        setTeams((prevTeams) => prevTeams.map((t) => (t.id === updatedTeam.id ? updatedTeam : t)));
        setAllTeams((prevAllTeams) =>
          prevAllTeams.map((t) => (t.id === updatedTeam.id ? updatedTeam : t)),
        );
      }
    } catch (e) {
      telemetry.captureError(e);
    }
  };

  const handleSelectAll = async () => {
    setLoading(true);
    const isAllOn = teams.every((team) => team.isSweepAppInstalled);
    try {
      const response = isAllOn
        ? await uninstallTeamsApp(allTeams)
        : await installTeamsApp(allTeams);
      setTeams(response);
      setAllTeams(response);
    } catch (e) {
      telemetry.captureError(e);
    } finally {
      setLoading(false);
    }
  };
  const [allTeams, setAllTeams] = useState<any[]>([]);

  const searchTeams = (value: string) => {
    if (!value.trim()) {
      return allTeams;
    }

    const searchTerm = value.toLowerCase();
    return allTeams.filter((team) => team.label.toLowerCase().includes(searchTerm));
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value;
    setSearch(searchValue);
    setSearching(true);

    setTimeout(() => {
      const filteredTeams = searchTeams(searchValue);
      setTeams(filteredTeams);
      setSearching(false);
    }, 300);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <SearchInput
        TextFieldProps={{
          value: search,
          autoFocus: true,
          onChange: handleSearch,
          placeholder: 'Search teams',
          sx: {
            width: '100%',
          },
        }}
        withFixedMagnifyingGlassIcon
        size="small"
        onClearButtonClick={() => {
          setSearch('');
          setTeams(allTeams);
        }}
      />
      <Typography variant="body">Toggle on the Teams you want to send alerts to.</Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          color: colors.grey[700],
        }}
      >
        <Typography variant="body">
          {`${teams?.length} ${pluralize('Team', teams?.length)}`}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body">Turn all on</Typography>
          <Switch
            onChange={handleSelectAll}
            disabled={loading}
            checked={teams.length > 0 && teams.every((team) => team.isSweepAppInstalled)}
          />
        </Box>
      </Box>

      {loading || searching ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
          <CenteredCircularProgress />
        </Box>
      ) : (
        <Stack>
          {teams.map((team) => (
            <Stack
              key={team.id}
              direction="row"
              alignItems="center"
              spacing={2}
              height={56}
              justifyContent="space-between"
              sx={{
                borderBottom: `1px solid ${colors.grey[120]}`,
              }}
            >
              <Typography variant="body">{team.label}</Typography>
              <Switch
                checked={team.isSweepAppInstalled}
                onChange={() => handleToggle(team)}
                disabled={loading}
              />
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
};
